import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Tooltip,
  useMediaQuery,
  Badge,
} from '@mui/material';
import { Download, UploadFile } from '@mui/icons-material';
import { DocumentOverview } from '../../Document/DocumentOverview';
import { ILegalRequirement } from '../../../../Interfaces/ILegalRequirement';
import { IDocument } from '../../../../Interfaces/IDocument';
import { LegalRequirementRequestFiles } from './LegalRequirementRequestFiles';
import { ILegalTransactionFullObject } from '../../../../Interfaces/ILegalTransaction';
import { LegalRequirementUploadFiles } from './LegalRequirementUploadFiles';
import { IConfig } from '../../../../Interfaces/IConfig';
import FolderIcon from '@mui/icons-material/Folder';

interface IProps {
  configObject: IConfig;
  legalTransactionObject: ILegalTransactionFullObject;
  legalRequirementObject: ILegalRequirement;
  isNotDisabled: boolean;
}

export const LegalRequirementDocuments: React.FC<IProps> = ({
                                                              configObject,
                                                              legalTransactionObject,
                                                              legalRequirementObject,
                                                              isNotDisabled,
                                                            }) => {
  const isDesktop = useMediaQuery('(min-width:600px)');
  const [documentArrayClient, setDocumentArrayClient] = useState<IDocument[]>(
    legalRequirementObject.DocumentArrayClient || []
  );
  const [documentArrayEmployees, setDocumentArrayEmployees] = useState<IDocument[]>(
    legalRequirementObject.DocumentArrayEmployees || []
  );

  const [isOpenClient, setIsOpenClient] = useState(false);
  const [isOpenEmployees, setIsOpenEmployees] = useState(false);
  const [isOpenUpload, setIsOpenUpload] = useState(false);

  const handleCloseClient = () => {
    const foundItem = documentArrayClient.find(x => x.idDocument < 0);

    if (!foundItem) {
      setIsOpenClient(false);
    } else {
      setIsOpenClient(false);
      setIsOpenUpload(true);
    }
  };

  const hanldeOpenEmployees = () => {
    setIsOpenEmployees(true);
  };

  // Lustige Upload / Download Icons
  const clickableIconSx = (theme: any) => ({
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    boxShadow: 1,
    transition: theme.transitions.create(['box-shadow', 'transform', 'background-color'], {
      duration: theme.transitions.duration.shortest,
    }),
    '&:hover': {
      boxShadow: 3,
      bgcolor: 'action.hover',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      boxShadow: 1,
      transform: 'translateY(0)',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  });

  return (
    <>
      <LegalRequirementUploadFiles
        idLegalTransaction={legalTransactionObject.idLegalTransaction}
        documentArrayClient={documentArrayClient}
        setDocumentArrayClient={setDocumentArrayClient}
        isOpen={isOpenUpload}
        setIsOpen={setIsOpenUpload}
      />

      <Dialog
        open={isOpenClient}
        onClose={handleCloseClient}
        maxWidth='lg'
        fullWidth
      >
        <DialogContent>
          <DocumentOverview
            key={`DocumentOverview-client`}
            configObject={configObject}
            legalTransactionObject={legalTransactionObject}
            idLegalRequirement={legalRequirementObject.idLegalRequirement}
            documentArray={documentArrayClient}
            setDocumentArray={setDocumentArrayClient}
            allowRemove={false}
            allowUpload
            title='Ihre Daten'
            showReadPermission
          />
        </DialogContent>
        <DialogActions>
          <Button variant='contained' onClick={handleCloseClient}>
            Okay
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isOpenEmployees}
        onClose={() => setIsOpenEmployees(false)}
        maxWidth='lg'
        fullWidth
      >
        <LegalRequirementRequestFiles
          configObject={configObject}
          legalTransaction={legalTransactionObject}
          documentArray={documentArrayEmployees}
          setIsOpen={setIsOpenEmployees}
        />
      </Dialog>

      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        sx={{
          whiteSpace: 'nowrap',
        }}
      >
        {legalRequirementObject.canUploadFilesClient && (
          <Tooltip title='Eigene Dateien hoch- & herunterladen'>
            <Badge
              badgeContent={documentArrayClient.length}
              showZero
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              sx={{
                mr: 1,
                ml: 1,
                '& .MuiBadge-badge': {
                  bgcolor: 'grey.500',
                  color: 'white',
                  fontSize: '0.7rem',
                  minWidth: 16,
                  height: 16,
                },
              }}
            >
              <IconButton
                aria-label="Dateien hochladen oder anzeigen"
                onClick={() => setIsOpenClient(true)}
                size="small"
                disabled={!isNotDisabled}
                sx={clickableIconSx}
              >
                <UploadFile fontSize="small" />
              </IconButton>
            </Badge>
          </Tooltip>
        )}

        {legalRequirementObject.canUploadFilesEmployees && (
          <Tooltip title='Bereitgestellte Dateien herunterladen'>
            <Badge
              badgeContent={documentArrayEmployees.length}
              showZero
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              sx={{
                mr: 1,
                '& .MuiBadge-badge': {
                  bgcolor: 'grey.500',
                  color: 'white',
                  fontSize: '0.7rem',
                  minWidth: 16,
                  height: 16,
                },
              }}
            >
              <IconButton
                aria-label="Bereitgestellte Dateien herunterladen"
                onClick={hanldeOpenEmployees}
                size="small"
                disabled={!isNotDisabled}
                sx={clickableIconSx}
              >
                <FolderIcon fontSize="small" />
              </IconButton>
            </Badge>
          </Tooltip>
        )}
      </Box>
    </>
  );
};
